/**
 * Room decor ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const roomDecorIdeasPack: ToilePack = {
    slug: 'room-decor-ideas',
    category: 'occasions',
    name: 'Room decor ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lights', label: 'Fairy lights', subject: 'a bedroom with fairy lights over the bed and photos pegged on them'},
        {key: 'posters', label: 'Poster wall', subject: 'a bedroom wall of posters in a grid'},
        {key: 'plants', label: 'Plant corner', subject: 'a corner with plants and a reading chair'},
    ],
    styles: [
        {key: 'cozy', label: 'Cozy', style: 'a cozy warm look at dusk, knitted throws, cushions and the fairy lights glowing softly', palette: 'warm beige, rust and cream', framing: 'an eye-level view, the corner filling the frame'},
        {key: 'minimal', label: 'Minimal', style: 'a clean minimal look with white walls, a few chosen pieces and bare floor', palette: 'white, light oak and soft grey', framing: 'a straight-on view with calm empty space around'},
        {key: 'boho', label: 'Boho', style: 'a boho look with rattan, macrame, a woven rug and layered textiles', palette: 'terracotta, mustard and sage green', framing: 'a three-quarter view, the room in layers'},
    ],
    directions: ['small room', 'anime posters', 'dark academia'],
    directionHint: 'try: small room',
    seo: {
        title: 'Room decor ideas: fairy lights, posters, plants',
        metaDescription: 'Nine room decor pictures on a budget: fairy lights over a bed, a poster wall and a plant corner, in cozy, minimal and boho looks.',
        h1: 'Room decor ideas: fairy lights, poster wall, plant corner',
        tagline: 'Nine pictures, three bedroom corners, each in a cozy, a minimal and a boho look.',
        intro: 'Room decor for a bedroom on a budget: fairy lights over the bed with photos pegged on them, a grid of posters on one wall, and a corner with plants and a reading chair. Each corner appears in a cozy look with warm throws and glowing lights, a minimal look with white walls and bare floor, and a boho look with rattan and macrame. Type a twist, such as small room or dark academia, and the same corners carry it.',
        useCases: [
            'a bedroom refresh on a small budget',
            'a shared room or dorm',
            "a photo wall for a teenager's room",
            'a reading corner with plants',
        ],
        faq: [
            {q: 'What are the three looks?', a: 'Cozy uses warm beige, rust and cream with throws and glowing fairy lights. Minimal uses white walls, light oak and bare floor. Boho uses rattan, macrame, a woven rug and terracotta, mustard and sage.'},
            {q: 'Are there people in the pictures?', a: 'No. The rooms are shown with no people.'},
            {q: 'Can I ask for a small room?', a: 'Yes. Type a twist such as small room or anime posters, and the same corners carry it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs of a room.'},
        ],
    },
}
